import { ServiceId } from "@mirage/discovery/id";
import * as services from "@mirage/discovery/services";
import { DashTrialInfoCache } from "@mirage/service-dash-trial/service/cache";
import type { APIv2Callable } from "@mirage/service-dbx-api/service";
import type { LogoutServiceConsumerContract } from "@mirage/service-logout";
import { getCombineAsyncRequestsFunc } from "@mirage/shared/util/combine-async-requests";
import { LargeKvStore } from "@mirage/storage/large-kv-store/interface";
import type { ConsolaInstance } from "consola";
import { BehaviorSubject, Observable } from "rxjs";
import { DashTrialNotificationsLastSeenCache } from "./notificationsCache";
import { DashTrialInfoSync } from "./sync";
import { DashTrialInfo, getTodaysDateString } from "./utils";

export type DashTrialNotificationsLastSeen = {
  chat: number;
  serp: number;
  omni: number;
  stacks: number;
  connector: number;
  modal: number;
};
export const DEFAULT_LAST_SEEN: DashTrialNotificationsLastSeen = {
  chat: 99,
  serp: 99,
  omni: 99,
  stacks: 99,
  connector: 99,
  modal: 99,
};

export type Service = {
  getDashTrialInfo(options?: {
    fetchAllData?: boolean;
  }): Promise<DashTrialInfo | undefined>;
  startSyncDashTrialInfo(): Promise<void>;
  getCurrentDashTrialInfo(): Observable<DashTrialInfo | undefined>;
  getDashTrialNotificationsLastSeen(): Promise<
    DashTrialNotificationsLastSeen | undefined
  >;
  setDashTrialNotificationsLastSeen(
    newState: DashTrialNotificationsLastSeen,
  ): Promise<void>;
};

interface DbxApiServiceContract {
  callApiV2: APIv2Callable;
}

interface LoggingServiceContract {
  tagged: (tag: string) => ConsolaInstance;
}

export default function provideDashTrialService(
  { callApiV2 }: DbxApiServiceContract,
  { tagged }: LoggingServiceContract,
  largeKvStore: LargeKvStore,
  logoutService: LogoutServiceConsumerContract,
) {
  const logger = tagged("service-dash-trial-info");
  const currentDashTrialInfo = new BehaviorSubject<DashTrialInfo | undefined>(
    undefined,
  );
  const cache = new DashTrialInfoCache(largeKvStore);
  const sync = new DashTrialInfoSync(
    cache,
    { callApiV2 },
    currentDashTrialInfo,
  );
  const notificationsCache = new DashTrialNotificationsLastSeenCache(
    largeKvStore,
  );
  async function getDashTrialInfo(): Promise<DashTrialInfo | undefined> {
    await sync.start();
    const dashTrialInfo = await cache.getDashTrialInfo();
    return dashTrialInfo?.dashTrialInfo;
  }

  async function startSyncDashTrialInfo(): Promise<void> {
    await sync.start();
  }

  function getCurrentDashTrialInfo(): Observable<DashTrialInfo | undefined> {
    return currentDashTrialInfo.asObservable();
  }

  async function tearDown() {
    sync.stop();
    await largeKvStore.clear();
  }

  const getUserMetadata = getCombineAsyncRequestsFunc((metadataKey: string) =>
    callApiV2("userMetadataUserMetadataGet", {
      metadata: [metadataKey],
    }),
  );
  const setUserMetadata = getCombineAsyncRequestsFunc(
    (metadata: { [key: string]: string }) =>
      callApiV2("userMetadataUserMetadataSet", {
        metadata,
      }),
  );

  logoutService.registerLogoutCallback(ServiceId.DASH_TRIAL_INFO, async () => {
    await tearDown();
  });

  async function getDashTrialNotificationsLastSeen(): Promise<
    DashTrialNotificationsLastSeen | undefined
  > {
    const cachedLastSeen =
      await notificationsCache.getDashTrialNotificationsLastSeen();
    const cacheDateUpdated = cachedLastSeen?.dateLastUpdated;

    // Update the cache if the date has changed
    if (cachedLastSeen && cacheDateUpdated === getTodaysDateString()) {
      return cachedLastSeen.lastSeen;
    }

    let parsedStr: string | null;
    let parsed: DashTrialNotificationsLastSeen;
    try {
      const userMetadata = await getUserMetadata(
        "DASH_TRIAL_ENDING_NOTIFICATIONS_LAST_SEEN",
      );
      const apiValue =
        userMetadata?.metadata?.["DASH_TRIAL_ENDING_NOTIFICATIONS_LAST_SEEN"];

      // We expect to receive the string "null" if not yet set.
      // Undefined and empty string are both treated as API misses and
      // will fail to parse, but we handle that below.
      parsedStr = JSON.parse(apiValue);
      if (!parsedStr) {
        return DEFAULT_LAST_SEEN;
      }

      // Double parse since the user_metadata API only supports primitive types.
      parsed = JSON.parse(parsedStr);
    } catch (error) {
      if (error instanceof SyntaxError) {
        logger.error(
          "Failed to parse DASH_TRIAL_ENDING_NOTIFICATIONS_LAST_SEEN value",
          error,
        );
      } else {
        logger.error(
          "getTrialNotificationLastSeen - Failed to fetch DASH_TRIAL_ENDING_NOTIFICATIONS_LAST_SEEN",
          error,
        );
      }
      return DEFAULT_LAST_SEEN;
    }

    notificationsCache.setDashTrialNotificationsLastSeen(parsed);
    return parsed;
  }

  // Persists state in both local storage and in server-side UserMetadata
  async function setDashTrialNotificationsLastSeen(
    newState: DashTrialNotificationsLastSeen,
  ): Promise<void> {
    try {
      // Double stringify since the user_metadata API only supports primitive types
      const stringified = JSON.stringify(JSON.stringify(newState));
      await setUserMetadata({
        DASH_TRIAL_ENDING_NOTIFICATIONS_LAST_SEEN: stringified,
      });

      await notificationsCache.setDashTrialNotificationsLastSeen(newState);
    } catch (error) {
      logger.error(
        "setTrialNotificationLastSeen - Failed to set DASH_TRIAL_ENDING_NOTIFICATIONS_LAST_SEEN",
        error,
      );
    }
  }

  return services.provide<Service>(
    ServiceId.DASH_TRIAL_INFO,
    {
      getDashTrialInfo,
      startSyncDashTrialInfo,
      getCurrentDashTrialInfo,
      getDashTrialNotificationsLastSeen,
      setDashTrialNotificationsLastSeen,
    },
    [ServiceId.DBX_API],
  );
}
